import { VisibilityType } from '../types/visibility.ts'
import { isPublicVolunteeringWeekDraftProject } from './publicProjectFilters.ts'

export const THIN_ORG_MIN_DESCRIPTION_LENGTH = 50

export type OrgSocialNetwork = 'facebook' | 'instagram' | 'linkedin'

const SOCIAL_NETWORK_DOMAINS: Record<OrgSocialNetwork, string[]> = {
    facebook: ['facebook.com', 'fb.com'],
    instagram: ['instagram.com'],
    linkedin: ['linkedin.com']
}

/** Org description (WYSIWYG HTML) as one line of plain text. */
export function orgDescriptionToPlainText(html?: string | null): string {
    return (typeof html === 'string' ? html : '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * A job, project or course that is or once was publicly listed: published now (or a public
 * volunteering-week draft), or published before and since closed, finished or hidden. Drafts and
 * link-only (unlisted) opportunities that were never published do not count.
 */
export function isPubliclyListedOpportunity(item: {
    visibility?: string | null
    published?: boolean | null
    publishedAt?: unknown
    unpublishedAt?: unknown
    finishedAt?: unknown
    finished?: boolean | null
    [key: string]: unknown
}): boolean {
    return (
        item.visibility === VisibilityType.published ||
        isPublicVolunteeringWeekDraftProject(item) ||
        item.published === true ||
        item.finished === true ||
        Boolean(item.publishedAt || item.unpublishedAt || item.finishedAt)
    )
}

/**
 * An org profile with nothing worth indexing: the /orgs/[id] page sends `noindex, follow` and the
 * sitemap leaves it out. That is a description under 50 characters once HTML is stripped and no
 * publicly listed opportunity, active or past — count the org's non-deleted jobs, projects and
 * skills that pass isPubliclyListedOpportunity.
 */
export function isThinOrg(desc: string | null | undefined, publicOpportunityCount: number): boolean {
    return publicOpportunityCount === 0 && orgDescriptionToPlainText(desc).length < THIN_ORG_MIN_DESCRIPTION_LENGTH
}

/**
 * Social profile links of an org, read from `org.social` (older documents kept them top-level).
 * The values are typed in free-form: full URLs, "facebook.com/x", bare handles, sometimes a name.
 * Whatever does not turn into a link to that network is left out.
 */
export function getOrgSocialLinks(
    org: { social?: Partial<Record<OrgSocialNetwork, string>> | null } & Partial<Record<OrgSocialNetwork, unknown>>
): { network: OrgSocialNetwork; url: string }[] {
    return (Object.keys(SOCIAL_NETWORK_DOMAINS) as OrgSocialNetwork[]).flatMap((network) => {
        const url = toSocialUrl(network, org.social?.[network] || org[network])
        return url ? [{ network, url }] : []
    })
}

function toSocialUrl(network: OrgSocialNetwork, value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined

    const trimmed = value.trim()
    if (!trimmed || /\s/.test(trimmed)) return undefined

    const domains = SOCIAL_NETWORK_DOMAINS[network]
    const isNetworkHost = (host: string) => domains.some((domain) => host === domain || host.endsWith(`.${domain}`))

    let candidate: string
    if (/^https?:\/\//i.test(trimmed)) {
        candidate = trimmed
    } else if (isNetworkHost(trimmed.split('/')[0]!.toLowerCase())) {
        candidate = `https://${trimmed}`
    } else if (network !== 'linkedin' && /^@?[\w.]+$/.test(trimmed) && !/^www\.|\.[a-z]{2,3}$/i.test(trimmed)) {
        // A bare handle, not a website ("www.x.sk") typed into the wrong field.
        // LinkedIn handles are ambiguous (/company/ vs /in/), so those are skipped.
        candidate = `https://www.${domains[0]}/${trimmed.replace(/^@/, '')}`
    } else {
        return undefined
    }

    try {
        const url = new URL(candidate)
        return isNetworkHost(url.hostname.toLowerCase()) ? url.href : undefined
    } catch {
        return undefined
    }
}
